/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useEffect, useRef } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { mixins, colors } from '@blend/consumer-components';

import { useRoom } from '../../../../hooks/useRoom';
import { useMediaPermissions } from '../../../../hooks/useDevice';
import { NetworkConnectionTestResults } from '../validators';
import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import { useSkipNetworkTests } from '../../../../hooks/useSkipNetworkTests';
import { NetworkResult } from './NetworkResult';
import { PreflightQualityScore } from './Preflight/getPreflightQualityScore';

export namespace NetworkConnection {
  export interface Props {
    onNetworkTestsCompletion: (results: NetworkConnectionTestResults) => void;
    showDeviceCompatibilityStatus?: boolean;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    textAlign: 'center',
    ...mixins.margin(24, 0, 0, 0),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  spinnerWrapper: {
    ...mixins.margin(16, 'auto', 10),
  },
  waitingMessage: {
    color: colors.catalystLight60,
    fontSize: 16,
    marginBottom: 0,
  },
});

export enum PreflightTestStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export const NetworkConnection: React.FC<NetworkConnection.Props> = ({
  onNetworkTestsCompletion,
  showDeviceCompatibilityStatus,
}) => {
  const skipNetworkTests = useSkipNetworkTests();
  const { videoProvider, isConnecting, isVideoConnectionBroken } = useVideoContext();
  const { roomId } = useRoom();
  const { videoEnabled, audioEnabled } = useMediaPermissions();
  const [preflightTestStatus, setPreflightTestStatus] = useState<PreflightTestStatus>(PreflightTestStatus.IN_PROGRESS);

  const hasPreflightRan = useRef<boolean>(false);

  const updatePreflightStatus = (preflightResults: VideoProvider.PreflightResults<unknown, PreflightQualityScore>) => {
    // do not update the status if the video connection is broken
    if (!preflightResults || isVideoConnectionBroken) {
      return;
    }

    const preflightStatus =
      preflightResults.qualityScore > PreflightQualityScore.Suboptimal || skipNetworkTests
        ? PreflightTestStatus.SUCCESS
        : PreflightTestStatus.FAILURE;

    setPreflightTestStatus(preflightStatus);
  };

  useEffect(() => {
    // if the video connection is broken, set the preflight status to failure
    if (isVideoConnectionBroken && preflightTestStatus !== PreflightTestStatus.FAILURE) {
      setPreflightTestStatus(PreflightTestStatus.FAILURE);
    }
  }, [isVideoConnectionBroken, preflightTestStatus]);

  useEffect(() => {
    const preflightCompleted = preflightTestStatus !== PreflightTestStatus.IN_PROGRESS;
    const preflightSuccess = preflightTestStatus === PreflightTestStatus.SUCCESS;

    onNetworkTestsCompletion({
      areAllTestsCompleted: preflightCompleted,
      allTestsPassed: preflightSuccess,
      audioEnabled,
      videoEnabled,
    });
  }, [preflightTestStatus, audioEnabled, videoEnabled]);

  const retriggerTests = () => {
    setPreflightTestStatus(PreflightTestStatus.IN_PROGRESS);
    videoProvider.runPreflightTest(roomId, updatePreflightStatus);
  };

  useEffect(() => {
    if (!isConnecting && !hasPreflightRan.current) {
      hasPreflightRan.current = true;
      videoProvider.runPreflightTest(roomId, updatePreflightStatus);
    }

    return () => {
      // Leave the room when the Network Connection component is unmounted
      // This is necessary to prevent the test video session from being left open
      videoProvider.leaveSession();
    };
  }, [isConnecting]);

  return (
    <div className={css(styles.wrapper)}>
      <NetworkResult
        preflightTestStatus={preflightTestStatus}
        retriggerTests={retriggerTests}
        audioEnabled={audioEnabled}
        videoEnabled={videoEnabled}
        isVideoConnectionBroken={isVideoConnectionBroken}
        showDeviceCompatibilityStatus={showDeviceCompatibilityStatus}
      />
    </div>
  );
};
